import React from 'react';
import { MapPin, Wifi } from 'lucide-react';

/**
 * Tag showing whether an interior designer declared they'll be available
 * ON-SITE (in person) for a given project phase. The flag is set per
 * phase-subscription when the designer subscribes to a project phase.
 *
 * Renders a green "Available on-site" pill when true. When false it renders a
 * neutral "Remote only" pill, unless `hideWhenRemote` is set (in which case it
 * renders nothing) — useful in dense card overlays where only the positive
 * signal is worth the space.
 *
 * Props:
 *  - isAvailableOnSite : boolean (undefined/null renders nothing)
 *  - hideWhenRemote    : when true, render nothing if not available on-site
 *  - size              : 'sm' (default) | 'xs' for compact card overlays
 *  - className         : optional extra classes
 */
const AvailabilityTag = ({
  isAvailableOnSite,
  hideWhenRemote = false,
  size = 'sm',
  className = '',
}) => {
  if (isAvailableOnSite == null) return null;
  if (!isAvailableOnSite && hideWhenRemote) return null;

  const pad = size === 'xs' ? 'px-2 py-0.5 text-[10px] gap-1' : 'px-3 py-1.5 text-xs gap-2';
  const iconSize = size === 'xs' ? 11 : 14;

  if (isAvailableOnSite) {
    return (
      <span
        className={`inline-flex items-center rounded-lg border border-green-200 bg-green-50 font-semibold text-green-800 ${pad} ${className}`}
      >
        <MapPin size={iconSize} className="shrink-0 text-green-600" aria-hidden />
        Available on-site
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 font-semibold text-gray-600 ${pad} ${className}`}
    >
      <Wifi size={iconSize} className="shrink-0 text-gray-400" aria-hidden />
      Remote only
    </span>
  );
};

export default AvailabilityTag;
